import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { createPresetCameraAngleAPI } from '../../../../../api/camera/presetCamera';
import {
  createProjectCameraAngleAPI,
  deleteProjectCameraAngleAPI,
  updateProjectCameraAngleAPI,
} from '../../../../../api/camera/projectCamera';
import { useUserContext } from '../../../../../contexts/UserContext';
import { decimals, isFalsyExcept0, objectToFormData } from '../../../../../helpers/jsHelper';
import { useCuratorData } from '../../../../../hooks/useCuratorData';
import { useInfoDialogaActions } from '../../../../../hooks/useInfoDialogaActions';
import { selectCameraPresetAnglesList } from '../../../../../redux/slicers/camera/cameraAngleSlicer';
import {
  selectCanvasSize,
  selectGlobalHeight,
  selectGlobalWidth,
} from '../../../../../redux/slicers/camera/cameraSettings';
import {
  addCameraSuccess,
  projectCameraAngleSelector,
  setProjectCameraAnglesList,
  setSubmittedOnce,
  updateCameraSuccess,
} from '../../../../../redux/slicers/camera/projectCameraAngleSlicer';
import { useUnityContext } from '../../../../container/unityContainer';
import { saveCurrentProjectCameras } from '../../../../../api/curator';
import { errorToast, successToast } from '../../../../../helpers/toastHelper';
import { MAX_RESOLUTION, MIN_RESOLUTION } from '../../../../../config/constant/unityConstants';
import {
  BLUR_INTENSITY_MAX_VALUE,
  BLUR_INTENSITY_MIN_VALUE,
  CAMERA_HEIGHT_MAX_VALUE,
  CAMERA_HEIGHT_MIN_VALUE,
} from '../../../../../config/constant/projectConstant';
import { UnityEvent, waitForEventOnce } from '../../../../../helpers/unityPubSub';
//import { captureException } from '@sentry/react';
import { getAppState } from '../../../../../redux/store';
import { isCameraNameValid } from '../../../../../helpers/unity/cameraHelper';
import { selectCalculatedRoomId } from '../../../../../redux/slicers/admin/curatorSlicer';
import { getMaxResolution } from '../../../../../helpers/unityHelper';
import { isUserOnboarding, setTooltipData } from '../../../../gamificationTooltip/helpers';

export const useCameraActions = () => {
  const dispatch = useDispatch();
  const { getProjectCameraAnglesList } = useSelector(projectCameraAngleSelector);
  const { type, roomId, isNewProjectFlow, isOpenProjectFlow } = useCuratorData();
  const { handleCreateCamera, unityTigg } = useUnityContext();
  const { openInfoDialog } = useInfoDialogaActions();
  const { currentUser } = useUserContext();
  const cameraPresetList = useSelector(selectCameraPresetAnglesList);
  const globalWidth = useSelector(selectGlobalWidth) || 1920;
  const globalHeight = useSelector(selectGlobalHeight) || 1080;

  const createCamera = async ({ name, onSuccess }) => {
    if (!name || !name.trim()) {
      errorToast('Please enter a camera name.');
      if (isUserOnboarding()) {
        setTooltipData({ step: 'GIVE_NAME_AND_SAVE' });
      }
      return;
    }

    if (
      getProjectCameraAnglesList?.find(
        (a) => a.cameraName.toLowerCase() === name.trim().toLowerCase()
      )
    ) {
      errorToast('Camera name already exist - please rename');
      dispatch(setSubmittedOnce(false));
      if (isUserOnboarding()) {
        setTooltipData({ step: 'GIVE_NAME_AND_SAVE' });
      }
      return;
    }

    unityTigg(); // TODO: check if we still need this function as it doesn't make sense to have it here
    const { cameraData } = await waitForEventOnce(UnityEvent.CameraDetailsReceiver, {
      triggerFunc: handleCreateCamera,
    });

    if (!cameraData) {
      errorToast('Error happened while creating camera');
      //captureException('Error happened while creating camera, LF_9823492374');
      if (isUserOnboarding()) {
        setTooltipData({ step: 'GIVE_NAME_AND_SAVE' });
      }
    }

    const camDetails = { ...cameraData };

    if (isNewProjectFlow) {
      const calculatedRoomId = selectCalculatedRoomId(getAppState());
      camDetails.room = Number.parseInt(calculatedRoomId);
    } else {
      camDetails.project = Number.parseInt(roomId);
      camDetails.id = uuidv4();
    }

    camDetails.resoX = globalWidth;
    camDetails.resoY = globalHeight;
    camDetails.fov = decimals(camDetails.fov, 3);
    camDetails.isCameraCorrection = camDetails.isCameraCorrection;
    camDetails.cameraHeight = decimals(camDetails.cameraHeight, 2);
    camDetails.isdepthOfField = camDetails.isdepthOfField;
    camDetails.blurIntensity = 5;
    camDetails.selectedObjectName = '';
    camDetails.focusXPos = '';
    camDetails.focusYPos = '';
    camDetails.focusZPos = '';
    camDetails.distanceToSet = decimals(camDetails.distanceToSet, 4);
    camDetails.is_lock = false;

    camDetails.focalLength = decimals(camDetails.focalLength, 4);
    camDetails.xTransRot = decimals(camDetails.xTransRot, 4);
    camDetails.yTransRot = decimals(camDetails.yTransRot, 4);
    camDetails.zTransRot = decimals(camDetails.zTransRot, 4);
    camDetails.xPos = decimals(camDetails.xPos, 4);
    camDetails.yPos = decimals(camDetails.yPos, 4);
    camDetails.zPos = decimals(camDetails.zPos, 4);
    camDetails.centerObjectxPos = decimals(camDetails.centerObjectxPos, 4);
    camDetails.centerObjectyPos = decimals(camDetails.centerObjectyPos, 4);
    camDetails.centerObjectzPos = decimals(camDetails.centerObjectzPos, 4);
    camDetails.cameraRotX = camDetails.cameraRotX;
    camDetails.cameraRotZ = camDetails.cameraRotZ;
    camDetails.lensShiftY = camDetails.lensShiftY;

    camDetails.cameraName = name;
    camDetails.member = currentUser.id;

    const canvasSize = selectCanvasSize(getAppState());
    camDetails.canvas_width = decimals(canvasSize.width || 0, 4);
    camDetails.canvas_height = decimals(canvasSize.height || 0, 4);
    camDetails.sensor_height = decimals(camDetails.sensor_height || 0, 4);
    camDetails.sensor_width = decimals(camDetails.sensor_width || 0, 4);

    if (isNewProjectFlow) {
      const formData = objectToFormData(camDetails);
      dispatch(createProjectCameraAngleAPI(formData, { onSuccess }));
      dispatch(setSubmittedOnce(false));
    } else {
      dispatch(addCameraSuccess(camDetails));
      if (!isUserOnboarding()) {
        successToast('Your camera has been succesfully added.', 'Camera Added');
      }
      dispatch(setSubmittedOnce(false));
      onSuccess();

      if (!isOpenProjectFlow) {
        saveCurrentProjectCameras();
      }
    }
  };

  const saveToPresets = ({ camera, onSuccess, onConflict }) => {
    const presetWithSameName = cameraPresetList.find(
      (p) => p.cameraName.toLowerCase() === camera.cameraName.toLowerCase()
    );

    if (presetWithSameName) {
      errorToast('Camera name already exist - please rename');
      onConflict?.();
      return;
    }

    const cameraCopy = {
      ...camera,
      member: currentUser.id,
      isdepthOfField: false,
      selectedObjectName: '',
      focusXPos: '',
      focusYPos: '',
      focusZPos: '',
    };
    delete cameraCopy.id;
    delete cameraCopy.project;
    delete cameraCopy.room;
    delete cameraCopy.render;
    delete cameraCopy.thumbnail;
    delete cameraCopy.file;
    delete cameraCopy.filmic_file;
    delete cameraCopy.filmic_thumbnail;
    // cameraCopy[type] = roomId,

    const calculatedRoomId = selectCalculatedRoomId(getAppState());

    (cameraCopy['room'] = calculatedRoomId),
      openInfoDialog({
        title: 'Add to Presets',
        description: `Do you want to add "${cameraCopy.cameraName} to presets?"`,
        cancelButtonText: 'No',
        buttonText: 'Yes',
        onButtonClick: () => {
          const formData = objectToFormData(cameraCopy);
          dispatch(
            createPresetCameraAngleAPI(formData, {
              onSuccess,
            })
          );
        },
      });
  };

  const updateCamera = ({ camera, newData, onSuccess, onValidationError }) => {
    newData = { ...newData };

    // when we have last saved camera and opened a new camera
    // and while auto saving camera it takes the last saved camera and replaced it with the opened camera
    // which lead to loss of camera that's why checking id
    if (camera.id !== newData.id) {
      onSuccess?.();
      return;
    }

    if (isEqual(camera, newData)) {
      onSuccess?.();
      return;
    }

    if (newData.project === null) {
      delete newData.project; // backend sends us project as null but not accept it back as null :(
    }

    const canvasSize = selectCanvasSize(getAppState());
    newData.canvas_width = canvasSize.width;
    newData.canvas_height = canvasSize.height;

    if (!isCameraValid(newData)) {
      onValidationError?.();
      return false;
    }

    if (isNewProjectFlow) {
      const formData = objectToFormData(newData);
      dispatch(updateProjectCameraAngleAPI(camera.id, formData, { onSuccess }));
    } else {
      successToast('Camera updated successfully!', {
        toastId: `CAMERA_UPDATE_SUCCESS_${newData.id}`,
      });
      dispatch(updateCameraSuccess(newData));
      onSuccess?.();

      if (!isOpenProjectFlow) {
        saveCurrentProjectCameras();
      }
    }
  };

  const deleteCamera = (camera, onSuccess) => {
    openInfoDialog({
      title: 'Delete',
      description: `Do you want to delete "${camera.cameraName}"`,
      cancelButtonText: 'No',
      buttonText: 'Yes',
      onButtonClick: () => {
        dispatch(
          setProjectCameraAnglesList([
            ...getProjectCameraAnglesList?.filter((a) => a.id != camera.id),
          ])
        );

        if (isNewProjectFlow) {
          // we need to delete it from the room. It's deleted from the project with line above and for the project we need to delete it only from the store
          deleteProjectCameraAngleAPI(camera.id);
        }

        if (!isOpenProjectFlow) {
          // update project json
          // updateProjectJson({ action: 'SAVE_CAMERAS' }) // something like this
          saveCurrentProjectCameras();
        }

        successToast('Camera deleted successfully!', {
          toastId: `CAMERA_DELETE_SUCCESS_${camera.id}`,
        });
        onSuccess?.();
      },
    });
  };

  return {
    createCamera,
    deleteCamera,
    saveToPresets,
    updateCamera,
  };
};

export const isCameraValid = (camera, { showToasts = true } = {}) => {
  const _errorToast = showToasts ? errorToast : () => { };
  const { maxXRes, maxYRes } = getMaxResolution();
  const cameraHeight = Number(camera.cameraHeight);

  const { empty, unique } = isCameraNameValid(camera.cameraName, camera.id, {
    returnType: 'object',
  });

  if (empty) {
    _errorToast('Camera name is missing');
    return false;
  }

  if (!unique) {
    _errorToast('Camera name is already taken');
    return false;
  }

  if (typeof camera.resoX !== 'number') {
    _errorToast('Camera width is missing');
    return false;
  }

  if (typeof camera.resoY !== 'number') {
    _errorToast('Camera height is missing');
    return false;
  }

  if (camera.resoX < MIN_RESOLUTION || camera.resoX > maxXRes) {
    _errorToast(`Camera width must be in range [${MIN_RESOLUTION}, ${maxXRes}]`);
    return false;
  }

  if (camera.resoY < MIN_RESOLUTION || camera.resoY > maxYRes) {
    _errorToast(`Camera height must be in range [${MIN_RESOLUTION}, ${maxYRes}]`);
    return false;
  }

  if (typeof camera.fov !== 'number') {
    _errorToast('Camera fov is missing');
    return false;
  }

  if (Number.isNaN(cameraHeight)) {
    _errorToast('Camera slide is missing');
    return false;
  }

  // if (camera.cameraHeight > CAMERA_HEIGHT_MAX_VALUE || camera.cameraHeight < CAMERA_HEIGHT_MIN_VALUE) {
  //   _errorToast(`Camera slide must be in range [${CAMERA_HEIGHT_MIN_VALUE}, ${CAMERA_HEIGHT_MAX_VALUE}]`) ;
  //   return false;
  // }

  // if (camera.fov > FOV_MAX_VALUE || camera.fov < FOV_MIN_VALUE) {
  //   _errorToast(`Camera fov must be in range [${FOV_MIN_VALUE}, ${FOV_MAX_VALUE}]`) ;
  //   return false;
  // }

  if (camera.isdepthOfField && isFalsyExcept0(camera.focusXPos)) {
    _errorToast('Please select a focus point for depth of field or press Esc to cancel');
    return false;
  }

  if (
    camera.isdepthOfField &&
    typeof camera.blurIntensity === 'string' &&
    camera.blurIntensity.trim() === ''
  ) {
    _errorToast('Camera blur intensity is missing');
    return false;
  }

  if (
    camera.blurIntensity > BLUR_INTENSITY_MAX_VALUE ||
    camera.blurIntensity < BLUR_INTENSITY_MIN_VALUE
  ) {
    _errorToast(
      `Blur intensity must be in range [${BLUR_INTENSITY_MIN_VALUE}, ${BLUR_INTENSITY_MAX_VALUE}]`
    );
    return false;
  }

  return true;
};
